import React, { useContext, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import '../../../css/Likert.scss';
import { LessonMode } from '../../../Constants';

import Guideline from '../tools/Guideline';

import HtmlComponent from '../HtmlComponent';
import OptionalImage from '../OptionalImage';

import responseService from '../../services/ResponseService';

import useStyleEvents from '../../../hooks/useStyleEvents';
import PrintQuestionNumber from './PrintQuestionNumber';

const getWidths = (model) => {
  let answerColumnWidths = [];
  if (model.answerColumnWidths === undefined) {
    if (model.options && model.options.length > 0) {
      for (let x = 0; x < model.options.length; x++) {
        answerColumnWidths.push('100');
      }
    }
    return answerColumnWidths;
  } else {
    answerColumnWidths = model.answerColumnWidths.map((w) => parseInt(w));
    return answerColumnWidths;
  }
};

const Likert = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    toolbarManager,
    responseManager,
  } = useContext(MobXProviderContext);

  // Define state
  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const { currentResponseAnswer } = lessonElementState;

  const readOnly = lessonElementState.readOnly || lessonManager.isReadOnly;

  const textBody = model.questionText || '';
  const tableHeader = model.answerTextHeader; // In the top left box
  const isMultiChoice = (model.answerType === 'mchoice' || model.answerType === undefined); // If false then this is multiselect
  const labelInCell = (model.answerPosition === 'cells' || model.answerPosition === undefined); // If this is false then the labels are in the header
  const { fixedWidths } = model;
  const { prompts } = model; // On the left side
  const hasPrompts = prompts && prompts.length === 1;
  const answerColumnWidths = getWidths(model);
  const answerOptions = model.options.filter((option) => option.label.length > 0); // Either in the header or in the cells
  const noHeader = (!tableHeader || tableHeader.length === 0) && (answerOptions.every((o) => o.length === 0) || labelInCell);

  // const isCorrectOption = (promptId, answerIndex) => {
  //   const correctAnswers = model.validation?.correctAnswers;
  //   if (!correctAnswers) {
  //     return false;
  //   }
  //   const promptIndex = prompts.findIndex((p) => p.id === promptId);
  //   if (correctAnswers[promptIndex]) {
  //     const correctAnswerIndices = correctAnswers[promptIndex].split(',').map((a) => parseInt(a));
  //     return correctAnswerIndices.includes(answerIndex);
  //   }
  //   return false;
  // };

  const isSelectedOption = (promptId, answerIndex) => {
    if (!currentResponseAnswer) {
      return false;
    }

    const selectedAnswers = currentResponseAnswer.answers[promptId];

    if (!selectedAnswers || selectedAnswers.index === undefined) {
      return false;
    }

    return selectedAnswers.index === answerIndex;
  };

  const onSelected = async (promptId, value, index) => {
    const data = { promptId, value, index };

    if (readOnly) {
      return;
    }

    await responseService.responseChangeHandler(data, lessonElementId);
  };

  useStyleEvents(lessonElementId);

  return (
    <div className='likert-question'>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <div className='test-item-question'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={textBody} />
      </div>
      <OptionalImage model={model} runtime={true} />
      <div className='test-item-answers'>
        <table className='likert-table' style={{ width: ((fixedWidths) ? 'auto' : '100%'), tableLayout: (fixedWidths) ? 'fixed' : '' }}>
          <thead>
            {!noHeader && (
              <tr>
                {answerOptions.map((answer, i) => (
                  <td key={i}
                    className={`likert-header answer-header ${ labelInCell ? 'empty' : 'full' }`}
                    style={{ width: fixedWidths ? answerColumnWidths[i] : 'auto' }}>
                    <HtmlComponent htmlStr={labelInCell ? '' : answer.label} />
                  </td>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {
              hasPrompts && prompts.map(({ id, text }) => (
                <RowContainer key={id}
                  isMultiChoice={isMultiChoice}
                >
                  {
                    answerOptions.map((answer, j) => (
                      <ChoiceCell key={j}
                        answer={answer.label}
                        answerIndex={j}
                        isMultiChoice={isMultiChoice}
                        isSelected={isSelectedOption(id, j)}
                        labelInCell={labelInCell}
                        onSelected={onSelected}
                        promptId={id}
                        readOnly={readOnly}
                        weight={answer.weight}
                      />
                    ))
                  }
                </RowContainer>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
});

const RowContainer = ({ isMultiChoice, children }) => {
  if (isMultiChoice) {
    // Then this should be a radio group
    return (
      <tr
        role='radiogroup'
      >{children}
      </tr>
    );
  } else {
    return <tr>{children}</tr>;
  }
};

const ChoiceCell = ({
  answer, answerIndex, isMultiChoice,
  isSelected, labelInCell, onSelected, promptId, readOnly, weight
}) => {
  const id = `${promptId}-${answer}`;
  const inputType = isMultiChoice ? 'radio' : 'checkbox';
  const symbolType = `${inputType }-symbol`;
  const tabIndex = 0;

  const visualClass = 'choice-input';

  const [focused, setFocused] = useState(false);

  const callOnSelected = (data) => {
    const { value } = data.target;
    onSelected(promptId, value, answerIndex);
  };

  return (
    <td className='likert-cell'>
      <label className='selector-container' htmlFor={id}>
        <div className='text-container'>
          {labelInCell ? <label htmlFor={id}><HtmlComponent htmlStr={answer} /></label> : ''}
          <label className={`symbol outline-separate ${readOnly ? 'disabled' : ''} ${isSelected ? 'selected' : ''} ${symbolType} ${visualClass} ${focused ? 'focused' : ''}`} htmlFor={id} />
          {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
          <input
            aria-checked={isSelected}
            checked={isSelected}
            className={visualClass} disabled={readOnly} id={id}
            name={promptId} onBlur={() => setFocused(false)} onChange={callOnSelected} onFocus={() => setFocused(true)}
            tabIndex={tabIndex} type={inputType} value={weight} />
        </div>
      </label>
    </td>
  );
};

export default Likert;
